import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import pxToRem from 'assets/theme/function/px-to-rem';

import { RowDataType, TableMetaData } from 'types/common/common';
import { ToolTintData } from 'types/toont-tint/toon-tint';
import { CardData } from 'types/card/card-list';
import { ImageTypeEnum } from 'types/card/card';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { openBackdrop } from 'store/slices/backdrop/backdrop-slice';

import { useLazyToonTintItemListQuery } from 'store/slices/toon-tint/toon-tint-item';

import { API_ENDPOINT_URL } from 'constant';

import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

import { StyledTableRow } from 'component/tabel-pagination-style';
import Filter from 'component/filter';
import AddItem from 'pages/toon-tint/toon-tint-item/add-item-toon-tint';
import EditItem from 'pages/toon-tint/toon-tint-item/edit-item-toon-tint';
import AddImages from 'component/add-images';
import ViewPhoto from 'component/view-photo';
import rgba from 'assets/theme/function/rgba';
import { ReactComponent as Plus } from 'assets/images/plus.svg';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import { selectCategoryIdOptions } from 'store/slices/toon-tint/toon-tint-category-id-slice';
import {
    useToonTintItemMainImageMutation,
    useToonTintItemMenuImageMutation,
} from 'store/slices/toon-tint/toon-tint-add-item-images';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import { useToonTintItemDeleteMutation } from 'store/slices/toon-tint/tool-tint-delete';
import { selectNewCardDataSliceOptions } from 'store/slices/cards/card/card-data-slice';

const defaultTableMeta: TableMetaData[] = [
    { key: 'img', position: 1, title: 'Img Item', isSortable: false },
    {
        key: 'itemPosition',
        position: 2,
        title: 'Position In Item',
        isSortable: false,
    },
    {
        key: 'isDefault',
        position: 3,
        title: 'Is Default',
        isSortable: false,
    },

    {
        key: 'productTitle',
        position: 3,
        title: 'Product Title',
        isSortable: false,
    },

    {
        key: 'createdAt',
        position: 7,
        title: 'Created Data',
        isSortable: true,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },
    { key: 'action', position: 5, title: 'Action', isSortable: false },
];

export type UrlObject = {
    url?: string;
    title?: string;
};

const ToonTintItem: React.FC = () => {
    const dispatch = useAppDispatch();
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [openEditItemDialog, setOpenEditItemDialog] = useState(false);
    const [openMainImgDialog, setOpenMainImgDialog] = useState(false);
    const [openMenuImgDialog, setOpenMenuImgDialog] = useState(false);
    const [itemId, setItemId] = useState<ToolTintData>();
    const [viewPhotoUrl, setViewPhotoUrl] = useState<UrlObject | null>(null);
    const [openAddItemDialog, setOpenAddItemDialog] = React.useState(false);

    const { categoryId } = useAppSelector(selectCategoryIdOptions);

    const [
        updateMainImagesApi,
        {
            isSuccess: imageMainIsSuccess,
            isError: imageMainIsError,
            error: imageMainError,
        },
    ] = useToonTintItemMainImageMutation();

    const [
        updateMenuImagesApi,
        {
            isSuccess: imageMenuIsSuccess,
            isError: imageMenuIsError,
            error: imageMenuError,
        },
    ] = useToonTintItemMenuImageMutation();

    const [
        deleteItemApi,
        {
            isSuccess: deleteIsSuccess,
            isError: deleteIsError,
            error: deleteError,
        },
    ] = useToonTintItemDeleteMutation();

    const [getToonTintList, { data, isFetching }] =
        useLazyToonTintItemListQuery();

    const { allowedGamesCard } = useAppSelector(selectNewCardDataSliceOptions);

    const flatAllowedGamesCard = allowedGamesCard?.flat() ?? [];

    const updateMainImages = (images: FormData) => {
        updateMainImagesApi({
            itemId: itemId?.id,
            image: images,
        });
    };

    const updateMenuImages = (images: FormData) => {
        updateMenuImagesApi({
            itemId: itemId?.id,
            image: images,
        });
    };

    const getToonTint = () => {
        getToonTintList(categoryId);
    };

    const deleteItem = ({ id }: CardData) => {
        if (id) {
            deleteItemApi(id);
        }
    };

    const handleClickEditItem = (data: ToolTintData) => {
        setItemId(data);
        setOpenEditItemDialog(true);
    };

    const createNewItem = () => {
        setOpenAddItemDialog(true);
    };

    const handleClickAddPhotoMain = (data: ToolTintData) => {
        setItemId(data);
        setOpenMainImgDialog(true);
    };

    const handleClickAddPhotoMenu = (data: ToolTintData) => {
        setItemId(data);
        setOpenMenuImgDialog(true);
    };

    useEffect(() => {
        const tableData: RowDataType[] = data
            ? data?.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      img: (
                          <>
                              {fullInfo?.menuFileId ? (
                                  <Box
                                      component="img"
                                      src={`${API_ENDPOINT_URL}/file/file/${fullInfo?.menuFileId}?source=${ImageTypeEnum.TOON_TINT_CATEGORY_ITEM}`}
                                      alt=""
                                      onClick={() => {
                                          setViewPhotoUrl({
                                              url: `${API_ENDPOINT_URL}/file/file/${fullInfo?.menuFileId}?source=${ImageTypeEnum.TOON_TINT_CATEGORY_ITEM}`,
                                          });
                                      }}
                                      sx={{
                                          cursor: 'pointer',
                                          width: 30,
                                          height: 30,
                                          objectFit: 'contain',
                                          objectPosition: 'top',
                                          borderRadius: borders.borderRadius.md,
                                      }}
                                  />
                              ) : (
                                  <Typography variant={'h5'}>-</Typography>
                              )}
                          </>
                      ),
                      action: flatAllowedGamesCard.includes('toon-tint') ? (
                          <Box />
                      ) : (
                          <Box
                              display="flex"
                              justifyContent="center"
                              gap={2}
                              onClick={(e) => e.stopPropagation()}
                          >
                              <Tooltip title="Delete">
                                  <Delete
                                      cursor="pointer"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          dispatch(
                                              openConfirmationDialogs({
                                                  textInfo:
                                                      'Are You Sure Want To Delete Category',
                                                  handleConfirm: deleteItem,
                                                  parameters: {
                                                      id: fullInfo.id,
                                                  },
                                              }),
                                          );
                                      }}
                                  />
                              </Tooltip>
                              <Tooltip title="Edit">
                                  <Edit
                                      cursor="pointer"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          handleClickEditItem({
                                              fileId: fullInfo.id,
                                              id: fullInfo.id,
                                          });
                                      }}
                                  />
                              </Tooltip>
                              <Tooltip title="Add photo main">
                                  <AddPhotoAlternateIcon
                                      titleAccess="Add photo main"
                                      cursor="pointer"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          handleClickAddPhotoMain({
                                              fileId: fullInfo.mainFileId,
                                              id: fullInfo.id,
                                              imageType:
                                                  ImageTypeEnum.TOON_TINT_CATEGORY_ITEM,
                                          });
                                      }}
                                  />
                              </Tooltip>

                              <Tooltip title="Add photo menu">
                                  <AddToPhotosIcon
                                      titleAccess="Add photo menu"
                                      cursor="pointer"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          handleClickAddPhotoMenu({
                                              fileId: fullInfo.menuFileId,
                                              id: fullInfo.id,
                                              imageType:
                                                  ImageTypeEnum.TOON_TINT_CATEGORY_ITEM,
                                          });
                                      }}
                                  />
                              </Tooltip>
                          </Box>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (categoryId) {
            getToonTint();
        }
    }, [categoryId]);

    useEffect(() => {
        if (deleteIsSuccess || imageMenuIsSuccess || imageMainIsSuccess) {
            setOpenMainImgDialog(false);
            setOpenMenuImgDialog(false);
            getToonTint();
        }
    }, [deleteIsSuccess, imageMenuIsSuccess, imageMainIsSuccess]);

    useEffect(() => {
        dispatch(openBackdrop({ open: isFetching }));
    }, [isFetching, dispatch]);

    useEffect(() => {
        const error = deleteError || imageMainError || imageMenuError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: { vertical: 'top', horizontal: 'right' },
                }),
            );
        }
    }, [deleteIsError, imageMainIsError, deleteError, imageMenuIsError]);

    return (
        <Box mt={pxToRem(15)}>
            <>
                <Filter name="Toon Tint Item" display={true} />
                <Box
                    bgcolor={colors.secondary.light}
                    borderRadius={borders.borderRadius.md}
                    mt={pxToRem(7)}
                    pb={pxToRem(20)}
                >
                    {flatAllowedGamesCard.includes('toon-tint') ? (
                        <Box />
                    ) : (
                        <Box
                            display={'flex'}
                            justifyContent={'end'}
                            alignItems={'center'}
                        >
                            <Button onClick={createNewItem} size={'small'}>
                                <Box display={'flex'} alignItems={'center'}>
                                    <Plus />
                                    <Typography
                                        pl={pxToRem(10)}
                                        color={rgba(colors.primary.main, 1)}
                                        variant={'subtitle1'}
                                    >
                                        Create New Item
                                    </Typography>
                                </Box>
                            </Button>
                        </Box>
                    )}

                    {tableData && tableData.length > 0 ? (
                        <TableContainer
                            sx={{
                                minHeight: pxToRem(150),
                                maxHeight: pxToRem(300),
                                overflowY: 'auto',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {defaultTableMeta.map((col) => (
                                            <TableCell key={col.key}>
                                                {col.title}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableData.map((row, idx) => (
                                        <StyledTableRow key={idx} data={row}>
                                            {defaultTableMeta.map((col) => {
                                                const value = row[col.key];

                                                let displayValue:
                                                    | string
                                                    | number
                                                    | null = value;

                                                if (
                                                    col.key === 'createdAt' &&
                                                    value
                                                ) {
                                                    displayValue =
                                                        dayjs(value).format(
                                                            'DD.MM.YYYY',
                                                        );
                                                } else if (
                                                    typeof value === 'boolean'
                                                ) {
                                                    displayValue = value
                                                        ? 'true'
                                                        : 'false';
                                                } else if (value == null) {
                                                    displayValue = '-';
                                                }

                                                return (
                                                    <TableCell key={col.key}>
                                                        {displayValue}
                                                    </TableCell>
                                                );
                                            })}
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography align="center" sx={{ mt: 2 }}>
                            No data available
                        </Typography>
                    )}
                </Box>
            </>

            <Dialog
                open={openAddItemDialog}
                onClose={() => setOpenAddItemDialog(false)}
            >
                <AddItem
                    refetch={getToonTint}
                    setOpenDialog={setOpenAddItemDialog}
                />
            </Dialog>

            <Dialog
                open={openMainImgDialog}
                onClose={() => setOpenMainImgDialog(false)}
            >
                <AddImages
                    imageUrl={[
                        itemId?.fileId
                            ? `${API_ENDPOINT_URL}/file/file/${itemId.fileId}?source=${itemId.imageType}`
                            : undefined,
                    ]}
                    setOpenImgDialog={setOpenMainImgDialog}
                    onSubmitFun={updateMainImages}
                    dialogTitle="ITEM MAIN IMAGE"
                />
            </Dialog>

            <Dialog
                open={openMenuImgDialog}
                onClose={() => setOpenMenuImgDialog(false)}
            >
                <AddImages
                    imageUrl={[
                        itemId?.fileId
                            ? `${API_ENDPOINT_URL}/file/file/${itemId.fileId}?source=${itemId.imageType}`
                            : undefined,
                    ]}
                    setOpenImgDialog={setOpenMenuImgDialog}
                    onSubmitFun={updateMenuImages}
                    dialogTitle="ITEM MENU IMAGE"
                />
            </Dialog>

            <Dialog
                open={openEditItemDialog}
                onClose={() => setOpenEditItemDialog(false)}
            >
                <EditItem
                    refetch={getToonTint}
                    itemId={itemId?.id ?? ''}
                    setOpenEditCategoryDialog={setOpenEditItemDialog}
                />
            </Dialog>

            {viewPhotoUrl && (
                <ViewPhoto
                    open={!!viewPhotoUrl}
                    setOpen={() => setViewPhotoUrl(null)}
                    imageUrl={viewPhotoUrl}
                />
            )}
        </Box>
    );
};

export default ToonTintItem;
